import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, X, ShieldStar } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "sws_admin_token";

export default function LegacyWallAdmin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [actionMsg, setActionMsg] = useState("");

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError("");
    try {
      const r = await fetch(`${BACKEND}/api/sacrifice/legacy-wall/pending`, {
        headers: { "X-Admin-Token": token },
      });
      if (r.status === 401 || r.status === 403) {
        // Auto-clear bad token so user can re-enter
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        throw new Error("Invalid admin token. Please enter the correct one.");
      }
      if (!r.ok) throw new Error("Could not load pending submissions.");
      const data = await r.json();
      setPending(data.entries || []);
    } catch (err) {
      setError(err.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const act = async (photoId, action) => {
    setActionMsg("");
    try {
      const r = await fetch(`${BACKEND}/api/sacrifice/legacy-wall/${photoId}/${action}`, {
        method: "POST",
        headers: { "X-Admin-Token": token },
      });
      if (!r.ok) throw new Error(`Failed to ${action}.`);
      setPending((prev) => prev.filter((p) => p.id !== photoId));
      setActionMsg(action === "approve" ? "Approved — now live on the Legacy Wall." : "Rejected.");
    } catch (err) {
      setActionMsg(err.message || `Failed to ${action}.`);
    }
  };

  const saveToken = (e) => {
    e.preventDefault();
    const t = e.currentTarget.elements.token.value.trim();
    if (!t) return;
    localStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };

  return (
    <main data-testid="legacy-admin-page" className="bg-[#0A0A0A] text-white font-body min-h-screen">
      <div className="border-b border-white/10 px-6 md:px-12 py-4 flex items-center justify-between">
        <Link to="/admin" className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/70 hover:text-[#B32027]">
          ← Main admin dashboard
        </Link>
        <Link to="/legacy-wall" className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#B32027] hover:text-white">
          View public wall →
        </Link>
      </div>

      <section className="px-6 md:px-12 py-12 md:py-16 max-w-5xl mx-auto">
        <p className="text-[#B32027] uppercase tracking-[0.5em] text-xs sm:text-sm font-bold mb-4">/ Admin · Legacy Wall</p>
        <h1 className="font-display font-black uppercase leading-[0.85] tracking-tighter text-4xl sm:text-6xl mb-3">
          Pending<br /><span className="text-[#B32027]">submissions.</span>
        </h1>
        <p className="text-white/70 text-base md:text-lg max-w-2xl mb-10">Review photo + story submissions before they go live on the public wall.</p>

        {!token && (
          <form onSubmit={saveToken} data-testid="admin-token-form" className="max-w-md space-y-3 border border-white/15 bg-[#0F0F0F] p-6 mb-10">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#B32027] mb-2">/ Admin login</p>
            <input
              required
              name="token"
              type="password"
              placeholder="Admin token"
              data-testid="admin-token-input"
              className="w-full bg-black border-2 border-white/20 px-4 py-3 text-white placeholder-white/40"
            />
            <button
              type="submit"
              data-testid="admin-token-submit"
              className="bg-[#B32027] hover:bg-[#D42730] text-white px-6 py-3 text-[11px] font-bold uppercase tracking-[0.25em] w-full"
            >
              Unlock dashboard
            </button>
            <p className="text-white/50 text-xs leading-snug">Set <code className="text-[#B32027]">ADMIN_TOKEN</code> in <code className="text-[#B32027]">/app/backend/.env</code> and use that same value here.</p>
          </form>
        )}

        {error && (
          <div data-testid="admin-error" className="border border-[#B32027] bg-[#B32027]/10 px-5 py-4 mb-8 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-white text-sm">{error}</p>
            <button
              onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setError(""); }}
              data-testid="admin-logout"
              className="text-[10px] uppercase tracking-[0.25em] font-bold text-white border-b border-white pb-0.5"
            >
              Clear token
            </button>
          </div>
        )}

        {actionMsg && (
          <p data-testid="admin-action-msg" className="text-[#B32027] text-sm font-bold uppercase tracking-[0.2em] mb-6">{actionMsg}</p>
        )}

        {token && loading && <p className="text-white/60 italic">Loading…</p>}

        {token && !loading && pending.length === 0 && !error && (
          <div data-testid="admin-empty" className="border border-white/10 bg-[#0F0F0F] p-10 text-center">
            <ShieldStar size={30} weight="fill" className="text-[#B32027] mx-auto mb-4" />
            <p className="font-display font-black uppercase tracking-tighter text-2xl">Nothing pending.</p>
            <p className="text-white/65 text-base mt-3">You're all caught up. New submissions will appear here for your review.</p>
          </div>
        )}

        {token && !loading && pending.length > 0 && (
          <ul data-testid="admin-pending-list" className="space-y-5">
            {pending.map((p) => (
              <li key={p.id} data-testid={`admin-pending-${p.id}`} className="border border-white/15 bg-[#0F0F0F] p-5 md:p-6 grid md:grid-cols-[200px_1fr_auto] gap-5 items-start">
                <div className="aspect-[4/5] bg-black overflow-hidden border border-white/10">
                  <img src={`${BACKEND}${p.url}`} alt="" className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#B32027] mb-1">/ Submitted by</p>
                  <p className="font-display font-black uppercase tracking-tighter text-xl mb-1">{p.submitter_name}</p>
                  <p className="text-white/55 text-sm break-all">{p.submitter_email}</p>
                  <p className="text-white/55 text-xs mt-1">Sharing: <span className="text-white">{p.sharing_consent}</span></p>
                  {p.moving_for_name && (
                    <div className="mt-4">
                      <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/55 mb-1">/ Moving for</p>
                      <p className="text-white font-semibold">{p.moving_for_name} {p.moving_for_relationship && <span className="text-white/55 font-normal">· {p.moving_for_relationship}</span>}</p>
                    </div>
                  )}
                  {p.caption && (
                    <div className="mt-4">
                      <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-white/55 mb-1">/ Story</p>
                      <p className="text-white/85 text-sm whitespace-pre-wrap leading-snug">{p.caption}</p>
                    </div>
                  )}
                </div>
                <div className="flex md:flex-col gap-2">
                  <button
                    onClick={() => act(p.id, "approve")}
                    data-testid={`approve-${p.id}`}
                    className="inline-flex items-center gap-2 bg-[#B32027] hover:bg-[#D42730] text-white px-5 py-3 text-[11px] font-bold uppercase tracking-[0.25em] whitespace-nowrap"
                  >
                    <Check size={13} weight="bold" /> Approve
                  </button>
                  <button
                    onClick={() => act(p.id, "reject")}
                    data-testid={`reject-${p.id}`}
                    className="inline-flex items-center gap-2 border-2 border-white/30 hover:border-white text-white px-5 py-3 text-[11px] font-bold uppercase tracking-[0.25em] whitespace-nowrap"
                  >
                    <X size={13} weight="bold" /> Reject
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
